/**
 * A PORTA ÚNICA da identidade.
 *
 * Login por código enviado por e-mail (`ADR-0011`): a pessoa informa o
 * login do IW, a API confirma a pessoa no IW e manda um código de 6 dígitos
 * para o e-mail cadastrado lá; com o código, a API emite a sessão. TODO o
 * resto do app fala só com a interface `ProvedorDeIdentidade` — nenhum outro
 * arquivo sabe como a sessão é carregada, nem que existe cookie.
 *
 * NÃO EXISTE TOKEN NESTE ARQUIVO, e é o ponto principal do desenho: o token
 * viaja num cookie `HttpOnly`, que o JavaScript não consegue ler. Por isso
 * não há `sessionStorage` aqui — o lint proíbe em todo o front, sem exceção.
 * XSS neste app não rouba sessão porque não há nada no DOM para roubar. O
 * código de acesso também não é guardado: vive só no campo, até ser enviado.
 */

import { aoExpirarSessao, requisitar } from '../../shared/api/clienteHttp';
import { ErroDaApi } from '../../shared/api/erros';

/** Papéis em pt-BR — é vocabulário de domínio, e aparece na tela. */
export type Papel = 'operacao' | 'contas_medicas' | 'coordenacao';

/**
 * O de-para entre o membro do enum C# (inglês, como a API serializa) e o
 * token pt-BR que a tela mostra. É a mesma fronteira de idioma que o
 * `DbTokens` faz do lado do servidor, e por isso ela é explícita aqui:
 * membro desconhecido vira erro visível, não `undefined` silencioso.
 */
const PAPEL_POR_MEMBRO: Readonly<Record<string, Papel>> = {
  Operations: 'operacao',
  MedicalAccounts: 'contas_medicas',
  Coordination: 'coordenacao',
};

/** Formato do código que chega por e-mail. */
const SEIS_DIGITOS = /^[0-9]{6}$/;

export interface Sessao {
  readonly id: string;
  /** O handle de login. Não é e-mail — ver `db/migrations/0008`. */
  readonly usuario: string;
  readonly nome: string;
  readonly papel: Papel;
  /** Núcleo cuja fila a pessoa vê. `null` = papel transversal, vê todos. */
  readonly nucleoId: string | null;
  readonly expiraEm: string;
}

/**
 * A primeira etapa respondida. É IGUAL exista a pessoa ou não: o desafio de
 * quem não existe só não abre nada.
 */
export interface DesafioDeAcesso {
  readonly desafio: string;
  /** A partir de quando a tela libera "reenviar código" (ISO 8601). */
  readonly reenvioEm: string;
}

/** A segunda etapa: o desafio da primeira e o código que chegou por e-mail. */
export interface Credenciais {
  readonly desafio: string;
  readonly codigo: string;
}

export class FalhaDeAutenticacao extends Error {
  /** O código de erro da API, quando veio dela (ex.: `ACCESS_CODE_EXPIRED`). */
  readonly codigo: string | null;

  constructor(mensagem: string, codigo: string | null = null) {
    super(mensagem);
    this.name = 'FalhaDeAutenticacao';
    this.codigo = codigo;
  }
}

export interface ProvedorDeIdentidade {
  /**
   * A sessão já conhecida, sem ir à rede. `null` = ninguém, ou ainda não
   * carregou — quem precisa distinguir os dois usa `carregar`.
   */
  obterSessao: () => Sessao | null;
  /**
   * Pergunta ao servidor quem está logado.
   *
   * Existe porque o cookie é `HttpOnly`: o front NÃO consegue saber se há
   * sessão sem perguntar — e é por isso que a árvore precisa de um estado de
   * "carregando" antes de decidir redirecionar.
   */
  carregar: () => Promise<Sessao | null>;
  /** Primeira etapa do login: pede o código para o login do IW. */
  solicitarCodigo: (usuario: string) => Promise<DesafioDeAcesso>;
  /** Segunda etapa: confere o código e abre a sessão. */
  autenticar: (credenciais: Credenciais) => Promise<Sessao>;
  encerrar: () => Promise<void>;
  /** Assina mudanças de sessão. Devolve a função de cancelar. */
  aoMudar: (ouvinte: (sessao: Sessao | null) => void) => () => void;
}

/** O que a API devolve em `POST /sessoes` e `GET /sessoes/atual`. */
interface RespostaDeSessao {
  userId: string;
  username: string;
  displayName: string;
  role: string;
  nucleusId: string | null;
  expiresAt: string;
}

/** O que a API devolve em `POST /sessoes/codigo`. */
interface RespostaDeDesafio {
  challengeId: string;
  resendAvailableAt: string;
}

function mapear(resposta: RespostaDeSessao): Sessao {
  const papel = PAPEL_POR_MEMBRO[resposta.role];
  if (papel === undefined) {
    // Alto e claro: papel novo no servidor sem par aqui é divergência de
    // vocabulário, e deixar virar `undefined` esconderia isso até alguém
    // reparar numa tela sem rótulo.
    throw new FalhaDeAutenticacao(`Papel desconhecido vindo da API: ${resposta.role}.`);
  }

  return {
    id: resposta.userId,
    usuario: resposta.username,
    nome: resposta.displayName,
    papel,
    nucleoId: resposta.nucleusId,
    expiraEm: resposta.expiresAt,
  };
}

/**
 * A mensagem vem do ErrorCatalog do servidor — o único lugar do sistema com
 * prosa em português. Reescrevê-la aqui criaria uma segunda verdade sobre o
 * mesmo erro.
 */
function comoFalha(causa: unknown): unknown {
  if (!(causa instanceof ErroDaApi)) return causa;

  // O texto do catálogo para IW_UNAVAILABLE ("a informação exibida pode estar
  // defasada") é de tela de dados, e no login não faz sentido: o que houve foi
  // não conseguir confirmar o login.
  if (causa.codigo === 'IW_UNAVAILABLE') {
    return new FalhaDeAutenticacao(
      'Não foi possível confirmar seu login agora. Tente de novo em instantes.',
      causa.codigo,
    );
  }

  return new FalhaDeAutenticacao(causa.message, causa.codigo);
}

export function criarProvedorDeSessao(): ProvedorDeIdentidade {
  const ouvintes = new Set<(sessao: Sessao | null) => void>();
  let sessaoAtual: Sessao | null = null;

  function definir(sessao: Sessao | null): void {
    sessaoAtual = sessao;
    for (const ouvinte of ouvintes) ouvinte(sessao);
  }

  // Sessão vencida em qualquer chamada (ociosidade de 60 min, `ADR-0011`):
  // zera aqui, e a guarda de rota leva ao login com o destino preservado.
  aoExpirarSessao(() => {
    if (sessaoAtual !== null) definir(null);
  });

  return {
    obterSessao: () => sessaoAtual,

    carregar: async () => {
      try {
        const sessao = mapear(await requisitar<RespostaDeSessao>('/sessoes/atual'));
        definir(sessao);
        return sessao;
      } catch {
        // 401 é a resposta NORMAL de quem não está logado — não é falha. API
        // fora do ar também não deve derrubar o boot: sem sessão, a pessoa
        // vai para o login, que é o comportamento certo nos dois casos.
        definir(null);
        return null;
      }
    },

    solicitarCodigo: async (usuario) => {
      const login = usuario.trim();
      if (login === '') {
        throw new FalhaDeAutenticacao('Informe o seu login do IW.');
      }

      try {
        const resposta = await requisitar<RespostaDeDesafio>('/sessoes/codigo', {
          method: 'POST',
          corpo: { usuario: login },
        });
        return { desafio: resposta.challengeId, reenvioEm: resposta.resendAvailableAt };
      } catch (causa) {
        throw comoFalha(causa);
      }
    },

    autenticar: async (credenciais) => {
      const codigo = credenciais.codigo.trim();
      if (!SEIS_DIGITOS.test(codigo)) {
        throw new FalhaDeAutenticacao('O código tem 6 dígitos. Confira o e-mail.');
      }

      let resposta: RespostaDeSessao;
      try {
        resposta = await requisitar<RespostaDeSessao>('/sessoes', {
          method: 'POST',
          corpo: { desafio: credenciais.desafio, codigo },
        });
      } catch (causa) {
        throw comoFalha(causa);
      }

      const sessao = mapear(resposta);
      definir(sessao);
      return sessao;
    },

    encerrar: async () => {
      try {
        // `unknown` e nao `void`: o 204 devolve corpo vazio, e o lint
        // recusa `void` como argumento de tipo.
        await requisitar<unknown>('/sessoes/atual', { method: 'DELETE' });
      } finally {
        // Local limpo mesmo se a chamada falhar: quem clicou em sair precisa
        // sair da tela. O cookie sobrevivendo a uma falha de rede é problema
        // do servidor, e a sessão dele continua rastreável no banco.
        definir(null);
      }
    },

    aoMudar: (ouvinte) => {
      ouvintes.add(ouvinte);
      return () => {
        ouvintes.delete(ouvinte);
      };
    },
  };
}

/** A instância que o app usa. Trocar aqui é trocar o modelo inteiro. */
export const provedorDeIdentidade: ProvedorDeIdentidade = criarProvedorDeSessao();
